import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import api from '../api/client';

/**
 * 본인 관리자 계정에 2FA 등록.
 *
 * 흐름:
 *   1) "QR 생성" → POST /admin/2fa/enroll → QR data URL + manualEntryKey 응답
 *   2) Google Authenticator 등에서 QR 스캔 (또는 manualEntryKey 수동 입력)
 *   3) 앱에 표시되는 6자리 코드 입력 → POST /admin/2fa/confirm
 *   4) 응답에서 백업코드 10개 평문 노출 — 사용자가 다운로드/인쇄 (절대 다시 못 봄)
 *   5) 완료
 *
 * 주의: 백업코드 응답은 1회만. 화면을 떠나면 평문 복구 불가.
 */
const TwoFactorSetupPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startEnroll = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin/2fa/enroll');
      setQrDataUrl(res.data.qrDataUrl);
      setManualEntryKey(res.data.manualEntryKey);
      setActiveStep(1);
    } catch (err) {
      setError(err?.response?.data?.message || '2FA 등록 시작에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('6자리 숫자 코드를 입력해주세요.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin/2fa/confirm', { code });
      setBackupCodes(res.data.backupCodes || []);
      setActiveStep(2);
    } catch (err) {
      setError(err?.response?.data?.message || '인증 코드가 올바르지 않습니다.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content =
      [
        'Mogoo 관리자 2FA 백업 코드',
        `발급일: ${new Date().toLocaleString('ko-KR')}`,
        '',
        '* 각 코드는 1회용입니다. 사용 즉시 만료됩니다.',
        '* Google Authenticator 등 인증 앱을 분실했을 때만 사용하세요.',
        '* 이 파일은 안전한 곳(금고, 1Password 등)에 보관하세요.',
        '',
        ...backupCodes.map((c, i) => `${(i + 1).toString().padStart(2, '0')}. ${c}`),
        '',
      ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mogoo-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        2단계 인증 (2FA) 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Google Authenticator, 1Password, Authy 등 TOTP 인증 앱과 연결합니다.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        <Step><StepLabel>시작</StepLabel></Step>
        <Step><StepLabel>QR 등록</StepLabel></Step>
        <Step><StepLabel>백업코드 저장</StepLabel></Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {activeStep === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              시작하기 전에 인증 앱이 설치되어 있는지 확인하세요.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              · Google Authenticator (iOS / Android)<br />
              · 1Password, Authy, Microsoft Authenticator 등 호환 앱
            </Typography>
            <Button variant="contained" onClick={startEnroll} disabled={loading}>
              {loading ? '준비 중...' : '시작'}
            </Button>
          </Box>
        )}

        {activeStep === 1 && (
          <Box component="form" onSubmit={confirmCode}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              아래 QR 코드를 인증 앱으로 스캔하세요.
            </Typography>
            {qrDataUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img src={qrDataUrl} alt="2FA QR" style={{ width: 220, height: 220 }} />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              QR 스캔이 어렵다면 아래 코드를 수동 입력하세요:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 3, fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all', backgroundColor: '#fafafa' }}>
              {manualEntryKey}
            </Paper>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              인증 앱에 표시되는 6자리 코드를 입력하세요:
            </Typography>
            <TextField
              label="6자리 코드"
              fullWidth
              autoFocus
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" disabled={loading} fullWidth>
              {loading ? '확인 중...' : '확인 및 활성화'}
            </Button>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              2FA가 활성화되었습니다.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>이 화면을 떠나면 백업 코드를 다시 볼 수 없습니다.</strong><br />
              지금 반드시 다운로드하거나 인쇄하여 안전한 곳에 보관하세요.
            </Alert>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              백업 코드 (각 코드는 1회용):
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                fontFamily: 'monospace',
                fontSize: 16,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' },
                gap: 1,
                backgroundColor: '#fafafa',
              }}
            >
              {backupCodes.map((c, i) => (
                <Box key={i} sx={{ textAlign: 'center', py: 0.5 }}>
                  {c}
                </Box>
              ))}
            </Paper>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={downloadBackupCodes}>
                .txt 다운로드
              </Button>
              <Button variant="outlined" onClick={() => window.print()}>
                인쇄
              </Button>
              <Button variant="text" onClick={() => navigate('/')}>
                완료
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TwoFactorSetupPage;
